/* ─────────────────────────────────────────────────────────
 * Offline Fallback Lessons — organised by skill level (1-5)
 * Syncs with backend when online; falls back to these offline.
 * ───────────────────────────────────────────────────────── */

import type { SkillLevel } from './skillLevel';

export interface LessonSummary {
  id: string;
  title: string;
  titleFr: string;
  difficulty: number;
  level: SkillLevel;
}

export interface LessonDetail {
  id: string;
  title: string;
  titleFr: string;
  level: SkillLevel;
  contentBlocks: Array<{ type: string; content: string; contentFr?: string; audioRef?: string }>;
}

export const FALLBACK_LESSONS: LessonDetail[] = [
  // ── Level 1: Starter ──
  {
    id: 'L1-01', title: 'Letter Sounds: A-E', titleFr: 'Sons des lettres : A-E', level: 1,
    contentBlocks: [
      { type: 'text', content: "Let's learn the sounds of letters A through E!", contentFr: "Apprenons les sons des lettres A à E !" },
      { type: 'word-tile', content: 'Apple', contentFr: 'Pomme' },
      { type: 'word-tile', content: 'Ball', contentFr: 'Balle' },
      { type: 'word-tile', content: 'Cat', contentFr: 'Chat' },
      { type: 'word-tile', content: 'Dog', contentFr: 'Chien' },
      { type: 'word-tile', content: 'Egg', contentFr: 'Oeuf' },
      { type: 'text', content: 'Great job! You learned 5 letter sounds! 🎉', contentFr: 'Bravo ! Tu as appris 5 sons de lettres ! 🎉' },
    ],
  },
  {
    id: 'L1-02', title: 'Letter Sounds: F-J', titleFr: 'Sons des lettres : F-J', level: 1,
    contentBlocks: [
      { type: 'text', content: "Now let's learn F through J!", contentFr: "Maintenant, apprenons F à J !" },
      { type: 'word-tile', content: 'Fish', contentFr: 'Poisson' },
      { type: 'word-tile', content: 'Girl', contentFr: 'Fille' },
      { type: 'word-tile', content: 'Hat', contentFr: 'Chapeau' },
      { type: 'word-tile', content: 'Ice', contentFr: 'Glace' },
      { type: 'word-tile', content: 'Jam', contentFr: 'Confiture' },
      { type: 'text', content: 'Amazing! 5 more sounds learned! ⭐', contentFr: 'Incroyable ! 5 sons de plus ! ⭐' },
    ],
  },
  {
    id: 'L1-03', title: 'Simple Words', titleFr: 'Mots simples', level: 1,
    contentBlocks: [
      { type: 'text', content: "Let's read some simple 3-letter words!", contentFr: "Lisons des mots simples de 3 lettres !" },
      { type: 'word-tile', content: 'Cat', contentFr: 'Chat' },
      { type: 'word-tile', content: 'Dog', contentFr: 'Chien' },
      { type: 'word-tile', content: 'Sun', contentFr: 'Soleil' },
      { type: 'word-tile', content: 'Run', contentFr: 'Courir' },
      { type: 'word-tile', content: 'Big', contentFr: 'Grand' },
      { type: 'text', content: 'You can read! Keep it up! 🌟', contentFr: 'Tu sais lire ! Continue ! 🌟' },
    ],
  },
  // ── Level 2: Explorer ──
  {
    id: 'L2-01', title: 'Sight Words: The Basics', titleFr: 'Mots fréquents : les bases', level: 2,
    contentBlocks: [
      { type: 'text', content: "Sight words are words we read quickly. Let's practice!", contentFr: "Les mots fréquents se lisent vite. Pratiquons !" },
      { type: 'word-tile', content: 'The', contentFr: 'Le/La' },
      { type: 'word-tile', content: 'And', contentFr: 'Et' },
      { type: 'word-tile', content: 'Is', contentFr: 'Est' },
      { type: 'word-tile', content: 'You', contentFr: 'Tu' },
      { type: 'word-tile', content: 'Can', contentFr: 'Peut' },
      { type: 'text', content: 'Now try: "You can read the book!" 📚', contentFr: 'Essaie : "Tu peux lire le livre !" 📚' },
    ],
  },
  {
    id: 'L2-02', title: 'Rhyming Words', titleFr: 'Mots qui riment', level: 2,
    contentBlocks: [
      { type: 'text', content: "Words that sound alike at the end are rhyming words!", contentFr: "Les mots qui finissent par le même son riment !" },
      { type: 'word-tile', content: 'Cat – Hat – Bat', contentFr: 'Chat – Rat – Mat' },
      { type: 'word-tile', content: 'Dog – Log – Fog', contentFr: 'Roi – Moi – Toi' },
      { type: 'word-tile', content: 'Sun – Fun – Run', contentFr: 'Jour – Tour – Four' },
      { type: 'text', content: 'Can you think of more rhyming words? 🎵', contentFr: 'Tu connais d\'autres rimes ? 🎵' },
    ],
  },
  {
    id: 'L2-03', title: 'Word Families: -at, -ig, -op', titleFr: 'Familles de mots', level: 2,
    contentBlocks: [
      { type: 'text', content: "Word families share the same ending sound!", contentFr: "Les familles de mots partagent la même fin !" },
      { type: 'word-tile', content: 'Cat, Bat, Hat, Sat', contentFr: 'Rat, Chat, Plat, Mat' },
      { type: 'word-tile', content: 'Big, Dig, Pig, Wig', contentFr: 'Gris, Pris, Prix, Cris' },
      { type: 'word-tile', content: 'Hop, Mop, Pop, Top', contentFr: 'Pot, Mot, Lot, Tôt' },
      { type: 'text', content: 'Word families help you read faster! 🚀', contentFr: 'Les familles de mots aident à lire plus vite ! 🚀' },
    ],
  },
  // ── Level 3: Builder ──
  {
    id: 'L3-01', title: 'Building Sentences', titleFr: 'Construire des phrases', level: 3,
    contentBlocks: [
      { type: 'text', content: "Let's put words together to make sentences!", contentFr: "Assemblons des mots pour faire des phrases !" },
      { type: 'text', content: 'The cat is big.', contentFr: 'Le chat est grand.' },
      { type: 'text', content: 'I can run fast.', contentFr: 'Je peux courir vite.' },
      { type: 'text', content: 'She has a red hat.', contentFr: 'Elle a un chapeau rouge.' },
      { type: 'text', content: 'We like to play.', contentFr: 'Nous aimons jouer.' },
      { type: 'text', content: 'Excellent reading! You made full sentences! ✨', contentFr: 'Excellente lecture ! Tu as lu des phrases complètes ! ✨' },
    ],
  },
  {
    id: 'L3-02', title: 'Spelling Practice', titleFr: 'Pratique d\'orthographe', level: 3,
    contentBlocks: [
      { type: 'text', content: "Can you spell these words? Say each letter!", contentFr: "Tu peux épeler ces mots ? Dis chaque lettre !" },
      { type: 'word-tile', content: 'House → H-O-U-S-E', contentFr: 'Maison → M-A-I-S-O-N' },
      { type: 'word-tile', content: 'School → S-C-H-O-O-L', contentFr: 'École → É-C-O-L-E' },
      { type: 'word-tile', content: 'Friend → F-R-I-E-N-D', contentFr: 'Ami → A-M-I' },
      { type: 'word-tile', content: 'Water → W-A-T-E-R', contentFr: 'Eau → E-A-U' },
      { type: 'text', content: 'Great spelling! Practice makes perfect! 🎯', contentFr: 'Super orthographe ! La pratique rend parfait ! 🎯' },
    ],
  },
  // ── Level 4: Reader ──
  {
    id: 'L4-01', title: 'Short Story: The Lost Puppy', titleFr: 'Histoire courte : Le chiot perdu', level: 4,
    contentBlocks: [
      { type: 'text', content: "Read this short story carefully!", contentFr: "Lis cette petite histoire attentivement !" },
      { type: 'text', content: 'Sam had a small puppy named Max. One day, Max ran away to the park.', contentFr: 'Sam avait un petit chiot nommé Max. Un jour, Max s\'est enfui au parc.' },
      { type: 'text', content: 'Sam looked everywhere. He checked behind the trees and under the benches.', contentFr: 'Sam a cherché partout. Il a regardé derrière les arbres et sous les bancs.' },
      { type: 'text', content: 'Finally, Sam found Max playing with another dog. "Come home, Max!" said Sam.', contentFr: 'Finalement, Sam a trouvé Max jouant avec un autre chien. "Rentre à la maison, Max !" a dit Sam.' },
      { type: 'text', content: 'Max wagged his tail and ran to Sam. They walked home together.', contentFr: 'Max a remué la queue et a couru vers Sam. Ils sont rentrés ensemble.' },
      { type: 'text', content: 'Question: Where did Sam find Max? 🤔', contentFr: 'Question : Où Sam a-t-il trouvé Max ? 🤔' },
    ],
  },
  {
    id: 'L4-02', title: 'Reading Comprehension', titleFr: 'Compréhension de lecture', level: 4,
    contentBlocks: [
      { type: 'text', content: "Read and think about what happens in the story.", contentFr: "Lis et réfléchis à ce qui se passe dans l'histoire." },
      { type: 'text', content: 'The bird sat on a branch. It sang a beautiful song. A cat heard the song and came closer.', contentFr: 'L\'oiseau était sur une branche. Il chantait une belle chanson. Un chat a entendu et s\'est approché.' },
      { type: 'text', content: 'The bird saw the cat and flew away quickly. The cat went back to sleep.', contentFr: 'L\'oiseau a vu le chat et s\'est envolé rapidement. Le chat s\'est rendormi.' },
      { type: 'text', content: 'Why did the bird fly away? What did the cat do after? 🧐', contentFr: 'Pourquoi l\'oiseau s\'est-il envolé ? Qu\'a fait le chat après ? 🧐' },
    ],
  },
  // ── Level 5: Champion ──
  {
    id: 'L5-01', title: 'Advanced Reading: Science Fun', titleFr: 'Lecture avancée : Sciences amusantes', level: 5,
    contentBlocks: [
      { type: 'text', content: "Let's read about something amazing in science!", contentFr: "Lisons quelque chose d'incroyable en science !" },
      { type: 'text', content: 'Did you know that water can be a solid, liquid, or gas? When water freezes, it becomes ice. When it boils, it becomes steam.', contentFr: 'Savais-tu que l\'eau peut être solide, liquide ou gazeuse ? Quand l\'eau gèle, elle devient glace. Quand elle bout, elle devient vapeur.' },
      { type: 'text', content: 'The sun heats the water in oceans and lakes. The water turns into tiny drops in the clouds. Then it falls back down as rain!', contentFr: 'Le soleil chauffe l\'eau des océans et des lacs. L\'eau se transforme en petites gouttes dans les nuages. Puis elle retombe en pluie !' },
      { type: 'text', content: 'This is called the water cycle. It happens over and over again.', contentFr: 'C\'est le cycle de l\'eau. Ça se répète encore et encore.' },
      { type: 'text', content: 'Can you explain the water cycle in your own words? 🌊', contentFr: 'Peux-tu expliquer le cycle de l\'eau avec tes mots ? 🌊' },
    ],
  },
  {
    id: 'L5-02', title: 'Creative Writing Prompts', titleFr: 'Écriture créative', level: 5,
    contentBlocks: [
      { type: 'text', content: "Time to write your own story! Pick a prompt:", contentFr: "C'est l'heure d'écrire ta propre histoire ! Choisis un sujet :" },
      { type: 'text', content: '1. "One morning, I woke up and discovered I could fly..."', contentFr: '1. "Un matin, je me suis réveillé et j\'ai découvert que je pouvais voler..."' },
      { type: 'text', content: '2. "The treasure map led to a secret cave under the school..."', contentFr: '2. "La carte au trésor menait à une grotte secrète sous l\'école..."' },
      { type: 'text', content: '3. "My robot friend and I went on an adventure to..."', contentFr: '3. "Mon ami robot et moi sommes partis à l\'aventure pour..."' },
      { type: 'text', content: 'Write at least 3 sentences for your story! ✍️', contentFr: 'Écris au moins 3 phrases pour ton histoire ! ✍️' },
    ],
  },
];

const MIN_READING_LESSONS_PER_LEVEL = 30;

const SUPPLEMENTAL_WORD_SETS: Record<SkillLevel, Array<{ skill: string; words: string[]; wordsFr: string[] }>> = {
  1: [
    { skill: 'short /a/ CVC words', words: ['mat', 'sat', 'cap', 'tap', 'ram', 'jam'], wordsFr: ['rat', 'sac', 'lac', 'mal', 'bar', 'car'] },
    { skill: 'short /i/ CVC words', words: ['sit', 'pin', 'lip', 'win', 'bin', 'fin'], wordsFr: ['fil', 'riz', 'lit', 'nid', 'cil', 'ski'] },
    { skill: 'short /o/ CVC words', words: ['hot', 'top', 'dot', 'hop', 'log', 'pot'], wordsFr: ['pot', 'bol', 'col', 'sol', 'roc', 'mot'] },
    { skill: 'short /e/ CVC words', words: ['bed', 'red', 'hen', 'ten', 'pen', 'web'], wordsFr: ['sel', 'bec', 'gel', 'mer', 'fer', 'sec'] },
    { skill: 'short /u/ CVC words', words: ['cup', 'sun', 'run', 'mud', 'bug', 'hut'], wordsFr: ['bus', 'mur', 'dur', 'jus', 'sur', 'pur'] },
  ],
  2: [
    { skill: 'L-blends', words: ['black', 'clap', 'flag', 'glad', 'plan', 'slip'], wordsFr: ['bleu', 'clap', 'flan', 'glas', 'plan', 'slim'] },
    { skill: 'R-blends', words: ['frog', 'drum', 'trip', 'grin', 'brag', 'crab'], wordsFr: ['gris', 'bras', 'crabe', 'drap', 'fret', 'trou'] },
    { skill: 'final blends', words: ['best', 'hand', 'jump', 'sink', 'belt', 'fast'], wordsFr: ['camp', 'banc', 'fort', 'grand', 'est', 'vent'] },
    { skill: 'digraphs sh/ch/th', words: ['ship', 'chin', 'thin', 'shop', 'chat', 'path'], wordsFr: ['chat', 'chou', 'thon', 'photo', 'chic', 'thé'] },
    { skill: 'mixed blends', words: ['branch', 'fresh', 'plant', 'splash', 'crisp', 'stamp'], wordsFr: ['branche', 'frais', 'plante', 'classe', 'crisp', 'timbre'] },
  ],
  3: [
    { skill: 'magic e words', words: ['make', 'bike', 'home', 'cube', 'shine', 'brave'], wordsFr: ['rose', 'lire', 'rime', 'cube', 'brave', 'ligne'] },
    { skill: 'vowel teams ai/ay', words: ['rain', 'train', 'play', 'day', 'paint', 'stay'], wordsFr: ['mais', 'paix', 'lait', 'vrai', 'clair', 'frais'] },
    { skill: 'vowel teams ee/ea', words: ['tree', 'green', 'beach', 'team', 'sleep', 'dream'], wordsFr: ['idée', 'neige', 'reine', 'peine', 'treize', 'aimer'] },
    { skill: 'long o teams', words: ['boat', 'road', 'snow', 'grow', 'coat', 'toast'], wordsFr: ['seau', 'beau', 'eau', 'rose', 'chaud', 'auto'] },
    { skill: 'controlled vowels', words: ['star', 'corn', 'bird', 'turn', 'farm', 'storm'], wordsFr: ['port', 'fort', 'soir', 'jour', 'court', 'four'] },
  ],
  4: [
    { skill: 'two syllable closed words', words: ['basket', 'rabbit', 'picnic', 'sunset', 'insect', 'napkin'], wordsFr: ['lapin', 'salade', 'cabane', 'minute', 'limite', 'pirate'] },
    { skill: 'compound words', words: ['sunset', 'cupcake', 'rainbow', 'bedroom', 'mailbox', 'snowman'], wordsFr: ['arc-en-ciel', 'tournesol', 'portefeuille', 'chou-fleur', 'gratte-ciel', 'grand-mère'] },
    { skill: 'prefix practice', words: ['unpack', 'replay', 'dislike', 'preview', 'return', 'unsafe'], wordsFr: ['refaire', 'défaire', 'revoir', 'prévoir', 'injuste', 'malheureux'] },
    { skill: 'suffix practice', words: ['helpful', 'jumping', 'kindness', 'quickly', 'teacher', 'smaller'], wordsFr: ['rapide', 'doucement', 'chanteur', 'grandeur', 'lecture', 'jardinier'] },
    { skill: 'syllable chunking', words: ['garden', 'tiger', 'robot', 'music', 'paper', 'winter'], wordsFr: ['jardin', 'tigre', 'robot', 'musique', 'papier', 'hiver'] },
  ],
  5: [
    { skill: 'three syllable words', words: ['butterfly', 'elephant', 'fantastic', 'computer', 'umbrella', 'volcano'], wordsFr: ['papillon', 'éléphant', 'fantastique', 'ordinateur', 'parapluie', 'volcan'] },
    { skill: 'four syllable words', words: ['information', 'community', 'celebration', 'imagination', 'responsible', 'impossible'], wordsFr: ['information', 'communauté', 'célébration', 'imagination', 'responsable', 'impossible'] },
    { skill: 'academic words', words: ['experiment', 'temperature', 'photograph', 'environment', 'opportunity', 'comfortable'], wordsFr: ['expérience', 'température', 'photographie', 'environnement', 'opportunité', 'confortable'] },
    { skill: 'advanced morphology', words: ['determination', 'collaboration', 'independence', 'understanding', 'appreciation', 'responsibility'], wordsFr: ['détermination', 'collaboration', 'indépendance', 'compréhension', 'appréciation', 'responsabilité'] },
    { skill: 'fluency challenge', words: ['extraordinary', 'encyclopedia', 'sophisticated', 'accomplishment', 'communication', 'organization'], wordsFr: ['extraordinaire', 'encyclopédie', 'sophistiqué', 'accomplissement', 'communication', 'organisation'] },
  ],
};

function buildSupplementalReadingLessons(): LessonDetail[] {
  const lessons: LessonDetail[] = [];
  for (let level = 1 as SkillLevel; level <= 5; level = (level + 1) as SkillLevel) {
    const existing = FALLBACK_LESSONS.filter(lesson => lesson.level === level).length;
    const needed = Math.max(0, MIN_READING_LESSONS_PER_LEVEL - existing);
    const wordSets = SUPPLEMENTAL_WORD_SETS[level];
    for (let i = 0; i < needed; i++) {
      const set = wordSets[i % wordSets.length];
      const lessonNumber = i + 1;
      lessons.push({
        id: `R${level}-${String(lessonNumber).padStart(2, '0')}`,
        title: `Reading Practice ${lessonNumber}: ${set.skill}`,
        titleFr: `Pratique de lecture ${lessonNumber} : ${set.skill}`,
        level,
        contentBlocks: [
          {
            type: 'text',
            content: `Structured literacy practice: ${set.skill}. Look, tap the sounds, then read each word.`,
            contentFr: `Pratique de littératie structurée : ${set.skill}. Regarde, tape les sons, puis lis chaque mot.`,
          },
          ...set.words.map((word, idx) => ({
            type: 'word-tile',
            content: word,
            contentFr: set.wordsFr[idx] ?? word,
          })),
          {
            type: 'text',
            content: 'Great work. You practiced with eyes, ears, voice, and tapping.',
            contentFr: 'Bravo. Tu as pratiqué avec les yeux, les oreilles, la voix et le tapotement.',
          },
        ],
      });
    }
  }
  return lessons;
}

FALLBACK_LESSONS.push(...buildSupplementalReadingLessons());

/** Get lessons filtered by skill level */
export function getLessonsForLevel(level: number): LessonSummary[] {
  return FALLBACK_LESSONS
    .filter(l => l.level <= level)
    .map(l => ({ id: l.id, title: l.title, titleFr: l.titleFr, difficulty: l.level, level: l.level }));
}

/** Get a specific lesson by ID */
export function getLessonById(id: string): LessonDetail | undefined {
  return FALLBACK_LESSONS.find(l => l.id === id);
}

/* ── Practice Reading Lesson Generator ── */
const READING_WORD_POOLS: Record<number, { en: string[]; fr: string[] }> = {
  1: {
    en: ['Apple', 'Ball', 'Cat', 'Dog', 'Egg', 'Fish', 'Girl', 'Hat', 'Ice', 'Jam', 'Kite', 'Lamp', 'Moon', 'Nest', 'Owl', 'Pen', 'Rain', 'Sun', 'Tree', 'Van'],
    fr: ['Pomme', 'Balle', 'Chat', 'Chien', 'Oeuf', 'Poisson', 'Fille', 'Chapeau', 'Glace', 'Confiture', 'Cerf-volant', 'Lampe', 'Lune', 'Nid', 'Hibou', 'Stylo', 'Pluie', 'Soleil', 'Arbre', 'Camion'],
  },
  2: {
    en: ['Hand', 'Book', 'Star', 'Cake', 'Lake', 'House', 'Flower', 'Cloud', 'Bread', 'Stone', 'Green', 'Smile', 'Dream', 'Light', 'World', 'Happy', 'Water', 'Tiger', 'Piano', 'Dance'],
    fr: ['Main', 'Livre', 'Étoile', 'Gâteau', 'Lac', 'Maison', 'Fleur', 'Nuage', 'Pain', 'Pierre', 'Vert', 'Sourire', 'Rêve', 'Lumière', 'Monde', 'Heureux', 'Eau', 'Tigre', 'Piano', 'Danse'],
  },
  3: {
    en: ['Beautiful', 'Adventure', 'Elephant', 'Birthday', 'Chocolate', 'Butterfly', 'Kangaroo', 'Dinosaur', 'Umbrella', 'Treasure', 'Mountain', 'Hospital', 'Sandwich', 'Suddenly', 'Together', 'Different'],
    fr: ['Magnifique', 'Aventure', 'Éléphant', 'Anniversaire', 'Chocolat', 'Papillon', 'Kangourou', 'Dinosaure', 'Parapluie', 'Trésor', 'Montagne', 'Hôpital', 'Sandwich', 'Soudain', 'Ensemble', 'Différent'],
  },
  4: {
    en: ['Environment', 'Imagination', 'Temperature', 'Photograph', 'Experiment', 'Underground', 'Communicate', 'Celebration', 'Opportunity', 'Comfortable'],
    fr: ['Environnement', 'Imagination', 'Température', 'Photographie', 'Expérience', 'Souterrain', 'Communiquer', 'Célébration', 'Opportunité', 'Confortable'],
  },
  5: {
    en: ['Responsibility', 'Extraordinary', 'Encyclopedia', 'Sophisticated', 'Accomplishment', 'Determination', 'Collaboration', 'Independence', 'Understanding', 'Appreciation'],
    fr: ['Responsabilité', 'Extraordinaire', 'Encyclopédie', 'Sophistiqué', 'Accomplissement', 'Détermination', 'Collaboration', 'Indépendance', 'Compréhension', 'Appréciation'],
  },
};

/* ── Original content backup for redo ── */
const originalReadingBlocks = new Map<string, LessonDetail['contentBlocks']>();
const regeneratedReadingLevels = new Set<number>();

export function isReadingLevelRegenerated(level: number): boolean {
  return regeneratedReadingLevels.has(level);
}

/** Check if a specific lesson (by ID) belongs to a regenerated level */
export function isLessonRegenerated(lessonId: string): boolean {
  const lesson = FALLBACK_LESSONS.find(l => l.id === lessonId);
  return lesson ? regeneratedReadingLevels.has(lesson.level) : false;
}

/**
 * Regenerate ALL lessons for a level with new random words.
 * Returns the first lesson ID so the caller can navigate to it.
 */
export function regenerateReadingLesson(level: number, count = 5, _locale = 'en'): string {
  const pool = READING_WORD_POOLS[level] || READING_WORD_POOLS[1];
  const lessons = FALLBACK_LESSONS.filter(l => l.level === level);
  if (lessons.length === 0) return '';

  for (const lesson of lessons) {
    if (!originalReadingBlocks.has(lesson.id)) {
      originalReadingBlocks.set(lesson.id, lesson.contentBlocks.map(b => ({ ...b })));
    }
    const enWords = [...pool.en].sort(() => Math.random() - 0.5).slice(0, count);
    const frWords = enWords.map(w => {
      const idx = pool.en.indexOf(w);
      return idx >= 0 ? pool.fr[idx] : w;
    });

    const newBlocks: LessonDetail['contentBlocks'] = [
      lesson.contentBlocks[0],
      ...enWords.map((word, i) => ({
        type: 'word-tile',
        content: word,
        contentFr: frWords[i],
      })),
      { type: 'text', content: 'Great reading practice! 🌟', contentFr: 'Belle pratique de lecture ! 🌟' },
    ];

    lesson.contentBlocks = newBlocks;
  }

  regeneratedReadingLevels.add(level);
  return lessons[0].id;
}

/** Restore original content for all lessons at a level */
export function restoreReadingLessons(level: number): void {
  const lessons = FALLBACK_LESSONS.filter(l => l.level === level);
  for (const lesson of lessons) {
    const orig = originalReadingBlocks.get(lesson.id);
    if (orig) {
      lesson.contentBlocks = orig.map(b => ({ ...b }));
    }
  }
  regeneratedReadingLevels.delete(level);
}
